/**
 * sessionTitles.js - neutral conversation titles for the advocate chat.
 *
 * PROD review 2026-07-30 #6 (trust-critical): conversation titles were the
 * verbatim first user message, so the sidebar replayed raw disclosures
 * ("I can't sleep since deployment", crisis language, health conditions) to
 * anyone glancing at the screen. Titles must never be raw user text.
 *
 * Two entry points:
 *   deriveSessionTitle(text, when) - build a NEUTRAL title for a new
 *     conversation from its first message: a topic bucket when keywords
 *     match, otherwise "Conversation - {d MMM}".
 *   neutraliseSessionTitle(stored, when, titleSource) - render-time guard
 *     for titles already stored in DynamoDB. Known-neutral titles pass
 *     through; anything containing sensitive vocabulary, or shaped like a
 *     verbatim utterance, is re-derived. Short, non-sensitive labels pass
 *     through unchanged. A title the veteran set through the rename dialog
 *     (titleSource 'user', UX-B0) is their own choice and is never rewritten.
 *
 * Mindframe note: bucket names are deliberately neutral. Crisis or mental
 * health vocabulary is never echoed back in a title.
 */

// ─── Topic buckets ────────────────────────────────────────────────────────────

export const TITLE_BUCKETS = {
  MENTAL_HEALTH: 'Mental health support',
  CLAIMS: 'Claims question',
  GENERAL: 'General support',
};

// Highest priority - any sensitive/mental-health vocabulary maps here and is
// ALWAYS neutralised at render time, whatever shape the stored title has.
const MENTAL_HEALTH_RE = new RegExp(
  [
    'suicid', 'self.?harm', 'kill (myself|me)', 'end (my|it) (life|all)',
    'not want to (live|be here)', 'ptsd', 'post.?traumatic', 'depress',
    'anxiet', 'anxious', 'panic', 'trauma', 'nightmare', 'flashback',
    'counsell', 'psycholog', 'psychiatr', 'mental health', 'overdose',
    'drinking', 'alcohol', '\\bdrugs?\\b', 'assault', 'abuse',
    'domestic violence', '\\banger\\b', '\\bangry\\b', 'grie[fv]',
    'lonel', 'hopeless', 'worthless', 'insomnia', "can'?t sleep", 'stress',
  ].join('|'),
  'i',
);

const CLAIMS_RE = new RegExp(
  [
    'claim', '\\bdva\\b', 'compensation', 'backdat', 'lodg', '\\bmrca\\b',
    '\\bvea\\b', '\\bdrca\\b', '\\bsrca\\b', 'permanent impairment',
    'incapacity', 'white card', 'gold card', 'entitlement', 'pension',
    'appeal', '\\bvrb\\b', '\\bart\\b', 'tribunal', 'review', 'reject',
    'denied', 'delegate', 'statement of principles', '\\bsop\\b',
    'needs assessment', 'back.?pay',
  ].join('|'),
  'i',
);

const GENERAL_RE = new RegExp(
  [
    'help', 'support', 'service', 'housing', 'homeless', '\\brent\\b',
    '\\bjob\\b', 'employ', '\\bwork\\b', 'transition', 'discharge', 'study',
    'course', 'money', 'financ', '\\bdebt\\b', 'family', 'relationship',
    'partner', '\\bkids?\\b', 'child', 'health', '\\bgp\\b', 'doctor',
    'medical', 'hospital', 'dental', 'hearing', 'tinnitus', 'injur',
    '\\bpain\\b',
  ].join('|'),
  'i',
);

/**
 * Map free text to a neutral topic bucket, or null when nothing matches.
 * @param {string} text
 * @returns {string|null}
 */
export function topicBucketFor(text) {
  const t = typeof text === 'string' ? text : '';
  if (!t.trim()) return null;
  if (MENTAL_HEALTH_RE.test(t)) return TITLE_BUCKETS.MENTAL_HEALTH;
  if (CLAIMS_RE.test(t)) return TITLE_BUCKETS.CLAIMS;
  if (GENERAL_RE.test(t)) return TITLE_BUCKETS.GENERAL;
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDayMonth(when) {
  const d = when instanceof Date && !Number.isNaN(when.getTime()) ? when : new Date();
  // Deterministic "{d MMM}" (AU English, no em dashes in UI copy) - Intl's
  // en-AU CLDR data leaves June/July unabbreviated and varies by ICU build.
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

function dateFallbackTitle(when) {
  return `Conversation - ${formatDayMonth(when)}`;
}

const NEUTRAL_TITLES = new Set([
  'Conversation',
  TITLE_BUCKETS.MENTAL_HEALTH,
  TITLE_BUCKETS.CLAIMS,
  TITLE_BUCKETS.GENERAL,
]);

function isNeutralTitle(t) {
  return NEUTRAL_TITLES.has(t) || /^Conversation - /.test(t);
}

/**
 * Heuristic: does a stored title look like a verbatim user utterance (the
 * pre-fix titles were the raw first message, truncated at 47 chars + '…')
 * rather than a deliberate short label typed into the rename dialog?
 */
function looksLikeUtterance(t) {
  if (t.length > 45) return true;
  if (/…/.test(t) || /\.\.\./.test(t) || /\?/.test(t)) return true;
  return /\b(i|i'm|im|i've|ive|my|me|we|can you|could you|please|help|need|want|tell me|what|how|why|when|where)\b/i.test(t);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Derive a NEUTRAL title for a new conversation. Never returns raw user text.
 *
 * @param {string} firstMessageText  the veteran's first message
 * @param {Date}   [when]            timestamp for the date fallback
 * @returns {string}
 */
export function deriveSessionTitle(firstMessageText, when = new Date()) {
  return topicBucketFor(firstMessageText) || dateFallbackTitle(when);
}

/**
 * Render-time neutraliser for titles already stored in the sessions table.
 * Old verbatim titles stop displaying raw disclosures; deliberate short
 * renames survive.
 *
 * UX-B0: a rename the veteran typed (stored with titleSource 'user') is shown
 * exactly as they wrote it. Before this, "My knee claim" was rewritten by the
 * utterance heuristic and "Stress claim" by the mental-health list. Every
 * other title is neutralised exactly as before.
 *
 * @param {string} storedTitle
 * @param {string|Date} [lastActivity]  used for the date fallback
 * @param {string} [titleSource]        'user' for a veteran's own rename
 * @returns {string}
 */
export function neutraliseSessionTitle(storedTitle, lastActivity, titleSource) {
  const when = lastActivity ? new Date(lastActivity) : new Date();
  const t = typeof storedTitle === 'string' ? storedTitle.trim() : '';
  if (!t) return dateFallbackTitle(when);
  if (titleSource === 'user') return t;
  if (isNeutralTitle(t)) return t;
  // Sensitive vocabulary is always neutralised, whatever the title's shape.
  if (MENTAL_HEALTH_RE.test(t)) return TITLE_BUCKETS.MENTAL_HEALTH;
  if (looksLikeUtterance(t)) return topicBucketFor(t) || dateFallbackTitle(when);
  // Short, non-sensitive label - most likely a deliberate rename.
  return t;
}
